import { cn } from '@/lib/utils'
import type { RouletteColor, RoulettePlacedChip } from '@/types/roulette'
import { AssetManager } from '../core/AssetManager'
import { ResultModal } from './ResultModal'
import { RouletteBettingBoard } from './RouletteBettingBoard'
import { RouletteWheel } from './RouletteWheel'

interface RouletteTableProps {
	pendingChips: RoulettePlacedChip[]
	hoveredPlace: string | null
	winningNumber: number
	winningColor: RouletteColor | null
	isWheelSpinning: boolean
	isResultVisible: boolean
	isDisabled: boolean
	multiplier: number
	profit: number
	currencyCode: string
	onHoverPlace: (place: string | null) => void
	onPlaceBet: (place: string) => void
	onUndo: () => void
	onClear: () => void
	onWheelComplete: () => void
	onDismissResult: () => void
}

export function RouletteTable({
	pendingChips,
	hoveredPlace,
	winningNumber,
	winningColor,
	isWheelSpinning,
	isResultVisible,
	isDisabled,
	multiplier,
	profit,
	currencyCode,
	onHoverPlace,
	onPlaceBet,
	onUndo,
	onClear,
	onWheelComplete,
	onDismissResult,
}: RouletteTableProps) {
	const outcomeColor = winningColor
	const outcomeGradient =
		outcomeColor === 'Red'
			? 'from-red-600 via-[#e79494] to-red-600'
			: outcomeColor === 'Black'
				? 'from-gray-800 via-gray-600 to-gray-800'
				: 'from-[#00ff15] via-[#92f09a] to-[#00ff15]'

	return (
		<div
			className='relative flex h-full min-h-0 w-full flex-col overflow-hidden rounded-xl bg-cover bg-center bg-no-repeat'
			style={{ backgroundImage: `url(${AssetManager.getImageUrl('tableFelt')})` }}
		>
			<ResultModal
				visible={isResultVisible}
				winningNumber={winningNumber}
				winningColor={winningColor}
				multiplier={multiplier}
				profit={profit}
				currencyCode={currencyCode}
				onDismiss={onDismissResult}
			/>

			<div className='flex min-h-0 flex-1 items-center justify-center overflow-hidden px-2 py-1 md:px-3 md:py-2'>
				<div className='relative aspect-square h-auto max-h-full w-[min(34%,132px)] md:w-[min(40%,250px)] [:fullscreen_&]:w-[min(50%,420px)]'>
					<RouletteWheel winningNumber={winningNumber} isSpinning={isWheelSpinning} onAnimationComplete={onWheelComplete} />
					<div
						className={cn(
							'absolute left-1/2 top-1/2 flex aspect-square w-[50%] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full bg-gradient-to-r shadow-lg transition-opacity duration-300 ease-out',
							outcomeGradient,
							isResultVisible ? 'opacity-95' : 'opacity-0',
						)}
					>
						<div className='text-xl font-bold text-white md:text-4xl'>{winningNumber >= 0 ? winningNumber : ''}</div>
						<div className='mt-0.5 text-[9px] uppercase text-white md:mt-1 md:text-sm'>{outcomeColor ?? ''}</div>
					</div>
				</div>
			</div>

			<div className='relative z-10 w-full shrink-0'>
				<RouletteBettingBoard
					pendingChips={pendingChips}
					hoveredPlace={hoveredPlace}
					winningNumber={winningNumber}
					isResultVisible={isResultVisible || (winningNumber >= 0 && !isWheelSpinning)}
					isDisabled={isDisabled}
					onHoverPlace={onHoverPlace}
					onPlaceBet={onPlaceBet}
					onUndo={onUndo}
					onClear={onClear}
				/>
			</div>
		</div>
	)
}
